import { autoinject, camelCase, TaskQueue } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { I18N } from 'aurelia-i18n';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { modelCodeToResourceKey } from 'app/shared/lib/i18n';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { AccountService, IUserNotificationSearchFilters } from 'app/core/services/account-service';
import { Toaster } from 'app/shared/lib/toaster';
import { SummaryLoader } from 'app/cross/services/summary-loader';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NotificationPreferenceReloadEvent } from 'app/core/lib/events';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import * as Core from 'app/core/models/essette-api';


@autoinject()
export class Notifications extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected userId: string | null = null;

    protected notifications: Core.UserNotification[] = [];
    protected selectedNotifications: Core.UserNotification[] = [];

    private notificationPreferencesReload: Subscription | null = null;

    protected dateEnd: string | null = null;
    protected dateStart: string | null = null;
    protected filters: IUserNotificationSearchFilters = {};
    protected sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected hasCaseRoute = false;
    protected hasAuthRoute = false;
    protected hasHouseholdCaseRoute = false;

    protected modelCodeToResourceKey = modelCodeToResourceKey;
    protected modelMatcher = modelIdMatcher;
    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<Core.UserNotification>;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly i18n: I18N,
        protected service: AccountService,
        private readonly queue: TaskQueue,
        protected readonly toaster: Toaster,
        private readonly summaryLoader: SummaryLoader,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);

        // initially filter within a one-month range
        const now = new DateTimeValue(this.env.now()).toParts();
        const lastMonth = new DateTimeValue(this.env.now()).toParts();
        lastMonth.month = lastMonth.month - 1;
        this.filters.dateStart = DateValue.fromParts(lastMonth).valueOf();
        this.filters.dateEnd = DateValue.fromParts(now).valueOf();
    }

    protected bind(): void {
        if(!this.userId) this.userId = this.env.user.id;
        this.reload();
        this.checkPermissions();
    }

    protected attached(): void {
        this.notificationPreferencesReload = this.events.subscribe(NotificationPreferenceReloadEvent, (e: NotificationPreferenceReloadEvent) => {
            this.reload();
        });
    }

    protected detached(): void {
        if(this.notificationPreferencesReload) {
            this.notificationPreferencesReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected selectChanged({ detail }: SelectEvent<Core.UserNotification>): void {
        // if detail is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.notifications.forEach(notification => {
                    const index = this.selectedNotifications.findIndex(
                        selected => selected.id === notification.id
                    );
                    if(index !== -1) {
                        this.selectedNotifications.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedNotifications.some(
                        notification => notification.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedNotifications.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id in detail (allows dups and id's correct service)
            if(match) {
                // add to selectedNotifications if not already present
                const alreadySelected = this.selectedNotifications.some(
                    notification => notification.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedNotifications.push(detail.item!);
                }
            } else {
                // remove from selectedNotifications if it exists based on the id of detail
                const index = this.selectedNotifications.findIndex(
                    notification => notification.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedNotifications.splice(index, 1);
                }
            }
        }
    }

    protected async submit(): Promise<void> {
        this.filters.dateStart = this.dateStart;
        this.filters.dateEnd = this.dateEnd;
        this.$table.deselectAll();
        this.selectedNotifications.splice(0);

        await this.loadAccountNotifications();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected async reload(): Promise<void> {
        await this.loadAccountNotifications();
    }

    protected buildMessage(item: Core.UserNotification): string {
        const code: string = item.type && item.type.code || '';
        const fallback: string = (item.type && item.type.description) || item.$display || '';
        if(code) {
            const parts = code.split('.');
            const key: string[] = [];
            for(let index = 0; index < parts.length; index++) {
                const part = parts[index];
                if(index === 1) {
                    key.push(':');
                }
                else if(index > 1) {
                    key.push('.');
                }
                if(index === 0) {
                    key.push(part.toLowerCase());
                }
                else {
                    key.push(camelCase(part));
                }
                if(index === 1) {
                    key.push('.notifications');
                }
            }
            const options = {
                defaultValue: fallback,
                what: item.argument && item.argument.$display || null,
                whom: item.metadata && item.metadata.triggered && item.metadata.triggered.byUser && item.metadata.triggered.byUser.$display || null,
                queue: item.queue && item.queue.$display || null
            };
            const value = this.i18n.tr(key.join(''), options);
            if(value) {
                return value;
            }
        }
        return fallback;
    }

    /** Returns wether or not a user has permission to open a summary */
    protected showLink(code: string): boolean {
        return this.summaryLoader.hasPermission(this.accountContext, code);
    }

    /** Opens the respective summary */
    protected openSummary(code: string, id: string): void {
        this.summaryLoader.openAttachedToSummary(code, id, this.hasCaseRoute, this.hasAuthRoute, this.hasHouseholdCaseRoute);
    }

    /** Returns an i18n tooltip string */
    protected attachedToToolTip(code: string): string {
        return this.summaryLoader.attachedToToolTip(code);
    }

    private restoreSelected() {
        if(!(this.selectedNotifications && this.selectedNotifications.length > 0)) return;
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(this.selectedNotifications);
        });
    }

    protected navigateTo(route: string): void {
        try {
            this.logger.debug(`Attempting to navigate to '${route}'.`);
            if(this.router.navigate(route)) return;
        }
        catch(error) {
            this.logger.error('Unable to navigate to Notification Target', error);
            this.toaster.warn().message('Unable to Navigate to Notification Target').show();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAccountNotifications(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        const includes: string[] = ['type', 'type.targetType', 'attachedTo', 'attachedTo.type', 'metadata.triggered.byUser', 'associatedEntity', 'associatedEntity.type'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAccountNotifications(options, includes, this.filters);
            this.notifications = response.results;
            this.resultCount = response.count || 0;
            this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async markNotificationsAs(state: 'read' | 'unread'): Promise<void> {
        if(!this.userId) return;
        const submittedNotifications: Core.UserNotification[] = [];
        this.selectedNotifications.forEach(n => submittedNotifications.push({ id: n.id, isRead: state === 'read' }));

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.markNotificationsRead(this.userId, submittedNotifications);
            isSuccessful = response.isSuccessful;
            this.selectedNotifications.splice(0);
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            const toast = isSuccessful ? this.toaster.info() : this.toaster.error();
            toast.message({
                key: state === 'read' ?
                    isSuccessful ? 'ui:messages.events.markedAsRead' : 'ui:messages.events.markedAsReadFailed' :
                    isSuccessful ? 'ui:messages.events.markedAsUnread' : 'ui:messages.events.markedAsUnreadFailed',
                what: 'core:userNotification.$_plural'
            }).show();
            this.state.endSave();
        }
    }

    @apiCall
    protected async markNotificationAsReadAndNavigateTo(item: Core.UserNotification): Promise<void> {
        if(!item || !item.id) return;
        try {
            this.state.beginSave();
            const response = await this.service.markNotificationRead(item.id);
            if(response.isSuccessful) {
                item.isRead = true;
                const result = response.result;
                if(result && typeof result.href === 'string') {
                    // append notification id to URL to force refresh
                    this.navigateTo(`${result.href}?notify=${item.id}`);
                }
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
